/**
 * Die einzige externe Veröffentlichungsfläche liegt am Objekt. Sie führt zusammen, was sonst über
 * Parent-Binding, Unit-Bindings, beide Publish-Outboxen und die Apartmentzeilen verteilt liegt.
 */
import { Button } from '@/shared/components';
import { isPlatformAdmin, isWorkspaceAdmin, useAuth } from '@/shared/auth';
import { useObjectDetail } from './ObjectDetailContext';
import { ObjectPublicationStatus } from './ObjectPublicationStatus';
import { PublicationUnitList } from './PublicationUnitList';
import { PublicationUnitRow } from './PublicationUnitRow';
import { publicationSummary } from './publicationSummary';
import { usePublicationCentre } from './usePublicationCentre';
import { usePublicationDialogs } from './usePublicationDialogs';

export function ObjectPublicationTab() {
  const { groupId, canEdit } = useObjectDetail();
  // Drei Rechte, nicht eines — so sagen es die Endpunkte: Pausieren/Fortsetzen darf jeder
  // Verwalter, veröffentlichen nur der Workspace-Admin, und der Abgleich schreibt eine
  // Vendor-Identität fest und ist PlatformAdmin-only. Ein Knopf, den der Server mit 403
  // beantwortet, ist dieselbe Lüge wie ein Häkchen, das nichts tut.
  const user = useAuth().user;
  const canPublish = isWorkspaceAdmin(user);
  const canReconcile = isPlatformAdmin(user);
  const centre = usePublicationCentre(groupId);
  const { data } = centre;
  const { dialogs, askLifecycle, askReconcile } = usePublicationDialogs(centre);

  if (centre.query.isLoading) {
    return <p className="text-ink-soft text-sm">Wird geladen…</p>;
  }
  if (centre.query.isError || !data) {
    return (
      <p className="text-sealwax text-sm">
        Der Veröffentlichungszustand konnte nicht geladen werden.
      </p>
    );
  }

  const busy = centre.publish.isPending || centre.lifecycle.isPending || centre.reconcile.isPending;
  const parentPublished = data.parentState === 'Published';
  const withSelect = canPublish && parentPublished && centre.selectable.length > 0;

  return (
    <div className="@container space-y-5">
      <div>
        <h2 className="text-ink font-serif text-2xl">Veröffentlichung</h2>
        <p className="text-ink-soft mt-1 text-sm">
          Wo die Apartments dieses Objekts verkauft werden.
        </p>
      </div>

      <ObjectPublicationStatus
        data={data}
        summary={publicationSummary(data)}
        actions={
          <>
            {canPublish && centre.action === 'parent' && (
              <Button
                variant="brand"
                className="min-h-11"
                disabled={busy}
                onClick={() => centre.publish.mutate('parent')}
              >
                {data.channexPropertyId ? 'Erneut übertragen' : 'Objekt veröffentlichen'}
              </Button>
            )}
            {canReconcile && data.parentState === 'NeedsReconcile' && data.parentBindingId && (
              <Button
                variant="brand"
                className="min-h-11"
                disabled={busy}
                onClick={() =>
                  askReconcile({
                    scope: 'parent',
                    bindingId: data.parentBindingId!,
                    label: data.name,
                  })
                }
              >
                Objekt abgleichen
              </Button>
            )}
          </>
        }
      />

      <PublicationUnitList
        units={data.units}
        withSelect={withSelect}
        hint={
          !data.channexPropertyId
            ? 'Die Apartments folgen, sobald das Objekt beim Kanalmanager steht.'
            : canPublish && !withSelect
              ? 'Neue Apartments erscheinen hier zur Auswahl.'
              : undefined
        }
        selection={
          withSelect && (
            <>
              <Button variant="ghost" className="min-h-11" onClick={centre.toggleAll}>
                {centre.allChosen ? 'Auswahl aufheben' : 'Alle offenen wählen'}
              </Button>
              <Button
                variant="brand"
                className="min-h-11"
                disabled={centre.action !== 'units' || busy}
                onClick={() => centre.publish.mutate('units')}
              >
                Auswahl veröffentlichen
              </Button>
            </>
          )
        }
        renderRow={(unit) => (
          <PublicationUnitRow
            key={unit.propertyId}
            unit={unit}
            withSelect={withSelect}
            checked={centre.effective.includes(unit.propertyId)}
            selectable={unit.canPublish && !busy}
            canEdit={canEdit}
            canReconcile={canReconcile}
            busy={busy}
            onToggle={() => centre.toggle(unit.propertyId)}
            onLifecycle={(action) => askLifecycle(unit, action)}
            onReconcile={() =>
              askReconcile({
                scope: 'unit',
                bindingId: unit.bindingId!,
                label: unit.name,
                resource: unit.ambiguousResource,
              })
            }
          />
        )}
      />

      {dialogs}
    </div>
  );
}
